import getPort from 'get-port'
import type z from 'zod'

import { tokenResponseSchema } from '@scalar-org/entities/token'

type TokenResult = z.infer<typeof tokenResponseSchema>

/**
 * Get a free port on the OS and starts a simple HTTP server on that port
 *
 * When the getTokens function is called the HTTP server is run until a request
 * is received. The token is then validated and the server is closed.
 *
 * To use this flow:
 * - Direct a user to login page and get an access token
 * - Use the exchangeToken endpoint to redirect to this server with
 *   the exchange token in the query parameters
 */
export const generateTokenCallbackEndpoint = async (
  /** URL that will be used to validate the exchange token */
  exchangeUrl: string,
): Promise<{
  port: number
  /** Cleans up the server without waiting for a response. */
  cancel: () => void
  getTokens: () => Promise<
    { data: TokenResult; error: false } | { data: null; error: true }
  >
}> => {
  const http = await import('node:http')
  const port = await getPort()

  const server = http.createServer()

  server.listen({ port, host: '0.0.0.0' })

  const getTokens = async (): Promise<
    { data: TokenResult; error: false } | { data: null; error: true }
  > => {
    const resultPromise = new Promise<
      { data: TokenResult; error: false } | { data: null; error: true }
    >((resolve) => {
      server.on('request', async (req, res) => {
        /** Return an error from the server */
        const errorResponse = (message: string, code: number) => {
          res.writeHead(code, {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          })
          res.end(
            JSON.stringify({
              success: false,
              error: true,
              message,
            }),
          )
          server.close()
          return resolve({ data: null, error: true })
        }

        if (req.method !== 'POST') {
          return errorResponse('Method not allowed', 405)
        }

        const url = new URL(`http://localhost:${port}${req.url}`)

        const exchangeToken = url.searchParams.get('exchangeToken')

        if (!exchangeToken) {
          return errorResponse('Missing exchange token', 400)
        }

        try {
          const tokens = await exchangeTokenForAccess(
            exchangeUrl,
            exchangeToken,
          )

          if (tokens) {
            res.writeHead(200, {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*',
            })
            res.end(
              JSON.stringify({
                success: true,
                error: false,
                message: 'Token exchanged. ',
              }),
            )
            server.close()
            return resolve({ data: tokens, error: false })
          }

          return errorResponse('Failed to exchange token', 400)
        } catch (err) {
          console.error(err)
          return errorResponse('Internal server error', 500)
        }
      })
    })

    const timeoutPromise = new Promise<{ data: null; error: true }>(
      (resolve) => {
        setTimeout(
          () => {
            server.close()
            resolve({ data: null, error: true })
          },
          3 * 60 * 1e3,
        )
      },
    )

    return Promise.race([resultPromise, timeoutPromise])
  }

  return {
    port,
    cancel: () => server.close(),
    getTokens,
  }
}

export const exchangeTokenForAccess = async (
  serverUrl: string,
  exchangeToken: string,
) => {
  const result = await fetch(serverUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      exchangeToken,
    }),
  })

  if (result.ok) {
    return tokenResponseSchema.parse(await result.json())
  }

  return null
}
